import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, Package, Tags, AlertTriangle, Calendar, AlertCircle } from 'lucide-react';
import { useInventory } from '../context/InventoryContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import ProductCard from '../components/products/ProductCard';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { isExpired, isExpiringWithinDays } from '../lib/dateUtils';
import { ManufacturedProduct, SupplyProductAllocation } from '../types';

const ProductsPage: React.FC = () => {
  const { products, categories, isLowStock, hasStockMismatch } = useInventory();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState(searchParams.get('filter') || 'all');
  const [expirationFilter, setExpirationFilter] = useState('all');
  const [allocationFilter, setAllocationFilter] = useState('all');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [manufacturedProducts, setManufacturedProducts] = useState<ManufacturedProduct[]>([]);
  const [allocations, setAllocations] = useState<SupplyProductAllocation[]>([]);
  
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      setStockFilter(filter);
      setIsFiltersVisible(true);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchManufacturedProducts();
    fetchAllocations();
  }, [user]);

  const fetchManufacturedProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('manufactured_products')
        .select('*')
        .order('name');

      if (error) throw error;
      setManufacturedProducts(data || []);
    } catch (error) {
      console.error('Error fetching manufactured products:', error);
    }
  };

  const fetchAllocations = async () => {
    try {
      const { data, error } = await supabase
        .from('supply_product_allocations')
        .select('*, manufactured_product:manufactured_products(id, name, sku, status)')
        .eq('is_active', true);

      if (error) throw error;
      setAllocations(data || []);
    } catch (error) {
      console.error('Error fetching allocations:', error);
    }
  };
  
  // Filter products based on search and filters
  const filteredProducts = products.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesCategory = !categoryFilter || product.category_id === categoryFilter;

    const matchesStock =
      stockFilter === 'all' ||
      (stockFilter === 'low' && isLowStock(product)) ||
      (stockFilter === 'out' && product.stock_quantity === 0) ||
      (stockFilter === 'mismatch' && hasStockMismatch(product));

    // Expiration filter logic
    const matchesExpiration =
      expirationFilter === 'all' ||
      (expirationFilter === 'expired' && isExpired(product.expiration_date)) ||
      (expirationFilter === 'expiring-week' && isExpiringWithinDays(product.expiration_date, 7)) ||
      (expirationFilter === 'expiring-soon' && isExpiringWithinDays(product.expiration_date, 30));

    // Allocation filter logic
    const productAllocations = allocations.filter(a => a.supply_id === product.id);
    const matchesAllocation =
      allocationFilter === 'all' ||
      (allocationFilter === 'unallocated' && productAllocations.length === 0) ||
      (allocationFilter !== 'all' && allocationFilter !== 'unallocated' &&
        productAllocations.some(a => a.manufactured_product_id === allocationFilter));

    return matchesSearch && matchesCategory && matchesStock && matchesExpiration && matchesAllocation;
  });
  
  const handleStockFilterChange = (value: string) => {
    setStockFilter(value);
    setSearchParams(params => {
      if (value === 'all') {
        params.delete('filter');
      } else {
        params.set('filter', value);
      }
      return params;
    });
  };
  
  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name,
    })),
  ];
  
  const stockOptions = [
    { value: 'all', label: 'All Stock Levels' },
    { value: 'low', label: 'Low Stock' },
    { value: 'out', label: 'Out of Stock' },
    { value: 'mismatch', label: 'Stock Mismatch' },
  ];

  const expirationOptions = [
    { value: 'all', label: 'All Items' },
    { value: 'expired', label: 'Expired' },
    { value: 'expiring-week', label: 'Expiring This Week' },
    { value: 'expiring-soon', label: 'Expiring Within 30 Days' },
  ];

  const allocationOptions = [
    { value: 'all', label: 'All Products' },
    { value: 'unallocated', label: 'Unallocated Supplies' },
    ...manufacturedProducts
      .filter(p => p.status !== 'discontinued')
      .map(product => ({
        value: product.id,
        label: product.name,
      })),
  ];
  
  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Industrial warehouse with organized supply inventory and storage systems"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Supplies</h1>
              <p className="text-lg opacity-90">
                Comprehensive supplies catalog and inventory management
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Package className="h-4 w-4 mr-2" />
                  <span>{products.length} Total Supplies</span>
                </div>
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <span>{products.filter(p => isLowStock(p)).length} Low Stock</span>
                </div>
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  <span>{products.filter(p => hasStockMismatch(p)).length} Stock Mismatch</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>{products.filter(p => isExpired(p.expiration_date) || isExpiringWithinDays(p.expiration_date, 30)).length} Expiring Soon</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <Link to="/supplies/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Supply
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-600" />
          </div>
          <Input
            type="search"
            placeholder="Search supplies..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
        </div>
        <Button 
          variant="secondary" 
          className="w-full sm:w-auto"
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>
      
      {isFiltersVisible && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Select
              label="Category"
              options={categoryOptions}
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value)}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Stock Level"
              options={stockOptions}
              value={stockFilter}
              onChange={handleStockFilterChange}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Expiration Status"
              options={expirationOptions}
              value={expirationFilter}
              onChange={(value) => setExpirationFilter(value)}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Allocated to Product"
              options={allocationOptions}
              value={allocationFilter}
              onChange={(value) => setAllocationFilter(value)}
              fullWidth
            />
          </div>
        </div>
      )}
      
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No supplies found</p>
          <Link to="/supplies/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Supply
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(product => {
            const productAllocations = allocations.filter(
              a => a.supply_id === product.id && a.is_active
            );

            return (
              <ProductCard
                key={product.id}
                product={product}
                allocations={productAllocations}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductsPage;